import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'
import { Search, SlidersHorizontal, ChevronLeft, ChevronRight, PackageSearch, X, Check, RotateCcw, Loader2 } from 'lucide-react'

import api from '../../../const/axios'
import { setPackages } from '../../../redux/actions'
import Package from '../../Elements/Package/Package'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { usePageParam } from '@/hooks/usePageParam'

// ─── Game type definitions ────────────────────────────────────────────────────

const GAME_TYPES = [
  { id: 1, label: 'Nə? Harada? Nə zaman?' },
  { id: 2, label: 'Xəmsə' },
  { id: 3, label: 'Fərdi Oyun' },
  { id: 4, label: 'Breyn Rinq' },
  { id: 5, label: 'Erudit-kvartet' },
  { id: 6, label: 'OSİP' },
  { id: 7, label: 'Quiz' },
]

const ALL_GAME_IDS = GAME_TYPES.map(g => g.id)

const PAGE_SIZE = 15

// ─── Filter chip ─────────────────────────────────────────────────────────────

const FilterChip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      'shrink-0 inline-flex items-center gap-1.5 h-8 px-3.5 !rounded-full text-sm font-medium border whitespace-nowrap transition-colors duration-150',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1',
      active
        ? 'bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800'
        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-800'
    )}
  >
    {active && <Check className="w-3.5 h-3.5" />}
    {children}
  </button>
)

// ─── Component ───────────────────────────────────────────────────────────────

const Packages: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  // Page lives in the URL (?page=N) so it survives back/forward navigation
  const { page, setPage, goToPage } = usePageParam()

  const [packages, setLocalPackages] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [keyword, setKeyword] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  // Empty selection means "all types" — no filter applied
  const [games, setGames] = useState<number[]>([])
  const [loading, setLoading] = useState(false)
  // Params actually used for fetching; keyword is debounced, types apply instantly
  const [committed, setCommitted] = useState<{ keyword: string; games: number[] }>({ keyword: '', games: [] })

  // True once the first response has arrived — before that we show skeletons
  // instead of the "nothing found" empty state
  const [loaded, setLoaded] = useState(false)
  // Incremented per request so a slow, outdated response can't overwrite newer results
  const requestIdRef = useRef(0)

  // Loading is kept local to the list (no global loader) so the search box and
  // filters stay interactive while results refresh
  const fetchPackages = useCallback(async (pg: number, params: { keyword: string; games: number[] }) => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    try {
      const gameIds = params.games.length ? params.games : ALL_GAME_IDS
      const res = await api.get(
        `/packages/forhome?page=${pg}&games=${gameIds}&word=${encodeURIComponent(params.keyword)}`
      )
      if (requestId !== requestIdRef.current) return
      dispatch(setPackages(res.data.packages) as any)
      setLocalPackages(res.data.packages)
      setTotal(res.data.count)
      setLoaded(true)
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }, [dispatch])

  useEffect(() => {
    fetchPackages(page, committed)
  }, [page, committed, fetchPackages])

  const apply = useCallback((next: { keyword: string; games: number[] }) => {
    setCommitted(prev =>
      prev.keyword === next.keyword && prev.games.join() === next.games.join() ? prev : next
    )
    setPage(1)
  }, [setPage])

  // Debounce typing so results update live without firing on every keystroke
  useEffect(() => {
    const trimmed = keyword.trim()
    if (trimmed === committed.keyword) return
    const id = setTimeout(() => apply({ keyword: trimmed, games }), 350)
    return () => clearTimeout(id)
  }, [keyword]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGame = (id: number) => {
    const next = games.includes(id) ? games.filter(g => g !== id) : [...games, id].sort()
    setGames(next)
    apply({ keyword: keyword.trim(), games: next })
  }

  const selectAll = () => {
    setGames([])
    apply({ keyword: keyword.trim(), games: [] })
  }

  const clearKeyword = () => {
    setKeyword('')
    apply({ keyword: '', games })
  }

  const resetFilters = () => {
    setKeyword('')
    setGames([])
    apply({ keyword: '', games: [] })
  }

  const hasFilters = committed.keyword !== '' || committed.games.length > 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>3sual.az | Sual paketləri</title>
        <meta name="robots" content="index" />
      </Helmet>

      {/* ── Page header ─────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-50 mb-6">
          {t('packages.allPackages')}
        </h1> */}

        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          {/* Search — live on desktop; on mobile an explicit submit button +
              keyboard "Search" key, which also dismisses the keyboard so the
              results become visible */}
          <form
            role="search"
            action="."
            onSubmit={e => {
              e.preventDefault()
              apply({ keyword: keyword.trim(), games })
              inputRef.current?.blur()
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
              <Input
                ref={inputRef}
                type="search"
                enterKeyHint="search"
                aria-label={t('packages.searchPackages')}
                className="pl-10 pr-10 h-11 !rounded-xl text-base sm:text-sm bg-gray-50 dark:bg-gray-950 border-gray-200 dark:border-gray-700 focus:bg-white dark:focus:bg-gray-900 focus-visible:ring-blue-400 focus-visible:ring-offset-0 transition-colors [&::-webkit-search-cancel-button]:hidden"
                placeholder={t('packages.searchPackages')}
                value={keyword}
                onChange={e => setKeyword(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape' && keyword) clearKeyword()
                }}
              />
              {keyword && (
                <button
                  type="button"
                  onClick={clearKeyword}
                  aria-label="Təmizlə"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 !rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-200 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              type="submit"
              aria-label="Axtar"
              className="sm:hidden h-11 w-11 p-0 shrink-0 !rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white"
            >
              <Search className="w-5 h-5" />
            </Button>
          </form>

          {/* Game type filter — empty selection = all */}
          <div className="mt-4 flex items-start gap-3">
            <span className="hidden sm:flex items-center gap-1.5 h-8 text-xs font-medium text-gray-500 dark:text-gray-400 shrink-0">
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Oyun növü
            </span>
            <div
              role="group"
              aria-label="Oyun növü"
              className="flex gap-2 overflow-x-auto sm:flex-wrap -mx-1 px-1 pb-1 sm:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              <FilterChip active={games.length === 0} onClick={selectAll}>
                Hamısı
              </FilterChip>
              {GAME_TYPES.map(g => (
                <FilterChip key={g.id} active={games.includes(g.id)} onClick={() => toggleGame(g.id)}>
                  {g.label}
                </FilterChip>
              ))}
            </div>
          </div>
        </div>

        {/* Result summary */}
        <div className="mt-4 flex items-center justify-between gap-3 min-h-[24px] text-sm">
          <p className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400" aria-live="polite">
            {loading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Yüklənir…
              </>
            ) : (
              <>
                <span className="font-semibold text-gray-800 dark:text-gray-100">{total}</span> paket tapıldı
              </>
            )}
          </p>
          {hasFilters && (
            <button
              type="button"
              onClick={resetFilters}
              className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Filtrləri sıfırla
            </button>
          )}
        </div>
      </div>

      {/* ── Package grid ─────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {packages.length > 0 ? (
          <>
            <div
              aria-busy={loading}
              className={cn(
                "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity duration-200",
                loading && "opacity-50 pointer-events-none"
              )}
            >
              {packages.map((item: any) => (
                <Link key={item.id} to={`/packages/${item.id}`} className="block">
                  <Package pack={item} free={true} />
                </Link>
              ))}
            </div>

            {/* ── Pagination ──────────────────────────────────────────── */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1}
                  className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:border-blue-400 dark:hover:border-blue-900 hover:text-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors dark:hover:text-indigo-200"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                  .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                    if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis')
                    acc.push(p)
                    return acc
                  }, [])
                  .map((p, i) =>
                    p === 'ellipsis' ? (
                      <span key={`e-${i}`} className="w-9 text-center text-gray-400 dark:text-gray-500 text-sm">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => goToPage(p as number)}
                        className={cn(
                          'w-9 h-9 rounded-lg text-sm font-medium border transition-colors',
                          page === p
                            ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-900 hover:text-blue-500 dark:hover:text-indigo-200'
                        )}
                      >
                        {p}
                      </button>
                    )
                  )}

                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page === totalPages}
                  className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:border-blue-400 dark:hover:border-blue-900 hover:text-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors dark:hover:text-indigo-200"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        ) : loading || !loaded ? (
          <div aria-busy="true" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }, (_, i) => (
              <div
                key={i}
                className="h-40 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 animate-pulse"
              >
                <div className="h-4 w-2/3 rounded bg-gray-200 dark:bg-gray-800" />
                <div className="mt-3 h-3 w-1/3 rounded bg-gray-100 dark:bg-gray-800/70" />
                <div className="mt-8 h-3 w-full rounded bg-gray-100 dark:bg-gray-800/70" />
                <div className="mt-2 h-3 w-4/5 rounded bg-gray-100 dark:bg-gray-800/70" />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <PackageSearch className="w-14 h-14 text-gray-200 mb-4" />
            <p className="text-gray-400 dark:text-gray-500 font-medium text-lg">Axtarışa uyğun paket tapılmadı</p>
            <p className="text-gray-300 text-sm mt-1">Başqa açar söz və ya növ seçin</p>
            {hasFilters && (
              <Button
                variant="outline"
                onClick={resetFilters}
                className="mt-5 !rounded-xl gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Filtrləri sıfırla
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Packages
